import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { updateCalendarDataSelfDate } from '../Redux/calendarDataSelfSlice';
import { incrementOperation, decrementOperation, setOperationError } from '../Redux/loadingSlice';
import { saveCalendarDataToCache, loadCalendarDataFromCache } from '../data/calendarCache';

/**
 * Hook to create, update, and delete calendar events.
 *
 * Each operation:
 *   1. Optimistically updates the events array in calendarDataSelf for the day
 *   2. Persists to Supabase calendar_events table
 *   3. Updates localStorage cache
 *   4. Uses the unified loading slice ('calendar' operation type)
 *
 * @returns {{ createEvent, updateEvent, deleteEvent }}
 */
export default function useCalendarEvent() {
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user.profile?.id);
  const reduxCalendar = useSelector((s) => s.calendarDataSelf.data);


  // ── Helper: update the events array for a single day in Redux + cache ──
  const updateDayEvents = (date, newEvents) => {
    const currentDayData = reduxCalendar?.[date] || {};
    const newDayData = { ...currentDayData, events: newEvents };

    // Optimistic Redux update
    dispatch(updateCalendarDataSelfDate({ date, data: newDayData }));

    // Update localStorage cache
    const cached = loadCalendarDataFromCache();
    if (cached?.data) {
      cached.data[date] = newDayData;
      saveCalendarDataToCache(cached);
    }
  };

  // ── Create event ─────────────────────────────────────────────────────
  const createEvent = async (date, { title, subtitle, contact_id, status, note }) => {
    if (!currentUserId) {
      return false;
    }

    const tempId = `temp-${Date.now()}`;
    let contactProfile = null;

    // Fetch contact profile if contact_id is provided
    if (contact_id) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', contact_id)
        .single();
      if (profileData) {
        contactProfile = profileData;
      }
    }

    const newEvent = { date, title, subtitle, contact: contact_id || null, contactProfile, status: status || 'pending', note: note || '' };

    // Optimistic: add to events array
    const currentEvents = reduxCalendar?.[date]?.events || [];
    updateDayEvents(date, [...currentEvents, { ...newEvent, id: tempId }]);

    dispatch(incrementOperation('calendar'));

    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .insert({
          user_id: currentUserId,
          day: date,
          title,
          subtitle,
          contact_id: contact_id || null,
          status: status || 'pending',
          note: note || '',
        })
        .select('id')
        .single();

      if (error) {
        updateDayEvents(date, currentEvents); // revert
        const msg = error.message?.toLowerCase();
        dispatch(setOperationError({ type: 'calendar', error: msg?.includes('network') || msg?.includes('fetch') ? 'Error: No internet connection' : 'Error: Failed to save' }));
        return false;
      }

      // Replace temp ID with real ID from Supabase
      const realId = data.id;
      const updatedEvents = [...currentEvents, { ...newEvent, id: realId }];
      updateDayEvents(date, updatedEvents);

      return realId;
    } catch (err) {
      updateDayEvents(date, currentEvents); // revert
      return false;
    } finally {
      dispatch(decrementOperation('calendar'));
    }
  };

  // ── Update event ─────────────────────────────────────────────────────
  const updateEvent = async (eventId, date, updates) => {
    if (!currentUserId || !eventId) {
      return false;
    }

    const currentEvents = reduxCalendar?.[date]?.events || [];
    const eventIndex = currentEvents.findIndex(e => e.id === eventId);
    if (eventIndex === -1) {
      return false;
    }

    // Optimistic: merge updates into the event
    const updatedEvents = currentEvents.map((ev, i) => {
      if (i === eventIndex) {
        const updated = { ...ev, ...updates };
        // If contact is being removed, also remove contactProfile if it exists
        if ('contact' in updates && updates.contact === null) {
          delete updated.contactProfile;
        }
        return updated;
      }
      return ev;
    });
    updateDayEvents(date, updatedEvents);

    dispatch(incrementOperation('calendar'));

    try {
      // Map frontend field names to Supabase column names
      const supabaseUpdates = {};
      if ('title' in updates) supabaseUpdates.title = updates.title;
      if ('subtitle' in updates) supabaseUpdates.subtitle = updates.subtitle;
      if ('contact' in updates) supabaseUpdates.contact_id = updates.contact;
      if ('status' in updates) supabaseUpdates.status = updates.status;
      if ('note' in updates) supabaseUpdates.note = updates.note;

      const { error } = await supabase
        .from('calendar_events')
        .update(supabaseUpdates)
        .eq('id', eventId);

      if (error) {
        updateDayEvents(date, currentEvents); // revert
        const msg = error.message?.toLowerCase();
        dispatch(setOperationError({ type: 'calendar', error: msg?.includes('network') || msg?.includes('fetch') ? 'Error: No internet connection' : 'Error: Failed to save' }));
        return false;
      }

      return true;
    } catch (err) {
      updateDayEvents(date, currentEvents); // revert
      return false;
    } finally {
      dispatch(decrementOperation('calendar'));
    }
  };

  // ── Delete event ─────────────────────────────────────────────────────
  const deleteEvent = async (eventId, date) => {
    if (!currentUserId || !eventId) {
      return false;
    }

    const currentEvents = reduxCalendar?.[date]?.events || [];

    // Optimistic: remove event from array
    const updatedEvents = currentEvents.filter(e => e.id !== eventId);
    updateDayEvents(date, updatedEvents);

    dispatch(incrementOperation('calendar'));

    try {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', eventId);

      if (error) {
        updateDayEvents(date, currentEvents); // revert
        const msg = error.message?.toLowerCase();
        dispatch(setOperationError({ type: 'calendar', error: msg?.includes('network') || msg?.includes('fetch') ? 'Error: No internet connection' : 'Error: Failed to save' }));
        return false;
      }

      return true;
    } catch (err) {
      updateDayEvents(date, currentEvents); // revert
      return false;
    } finally {
      dispatch(decrementOperation('calendar'));
    }
  };

  return { createEvent, updateEvent, deleteEvent };
}
